import { withSubmittedProgress } from "@/utils/bankProgress.mjs";
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useCurrentUser, useDocument, useFirestore } from "vuefire";
import { collection, doc } from "firebase/firestore";
import { useAlertStore } from "./alert";
import { useBankStore } from "./bank";
import { getFunctions, httpsCallable } from "firebase/functions";
import { withPerfTrace } from "@/utils/routeProfiler.mjs";

const functions = getFunctions();
functions.region = "europe-west2";

const GET_USER_QUESTIONS_WITH_META = httpsCallable(
  functions,
  "getUserQuestionsWithMeta",
);
// NEW: get category totals and high-yield counts
const GET_CATEGORY_YIELD_COUNTS = httpsCallable(
  functions,
  "getCategoryYieldCounts",
);
const END_SESSION = httpsCallable(functions, "endSession");

export const useStatsStore = defineStore("stats", () => {
  const db = useFirestore();
  const currentUser = useCurrentUser();
  const alertStore = useAlertStore();
  const bankStore = useBankStore();

  const userData = useDocument(
    doc(collection(db, "users"), currentUser.value.uid),
  );

  const metaStats = useDocument(
    computed(() => {
      const path =
        bankStore.currentBank === "part-2-question-bank"
          ? "meta/stats"
          : `new_modules/${bankStore.currentBank}/meta/stats`;
      return doc(db, path);
    }),
  );

  const userStats = computed(() => {
    switch (bankStore.currentBank) {
      case "part-2-question-bank":
        return withSubmittedProgress(userData.value?.stats);
      case "part-1-question-bank":
        return withSubmittedProgress(userData.value?.part1Stats);
      case "duke-elder-question-bank":
        return withSubmittedProgress(userData.value?.dukeElderStats);
      default:
        return withSubmittedProgress(userData.value?.stats);
    }
  });

  const userQuestions = computed(() => {
    const answeredQuestions = userStats.value?.questions;
    if (!answeredQuestions) return [];
    return Object.keys(answeredQuestions).map((key) => {
      answeredQuestions[key].qId = key;
      return answeredQuestions[key];
    });
  });

  const flaggedQuestionIds = computed(() =>
    userQuestions.value.filter((q) => q.flagged).map((q) => q.qId),
  );

  // NEW: holds { [categoryId]: { total: number, high: number } }
  const categoryYieldCounts = ref({});

  const getUserQuestionsWithMeta = async () => {
    try {
      const bank = bankStore.currentBank;
      let path = "";
      if (bank === "part-1-question-bank")
        path = "/new_modules/part-1-question-bank/";
      else if (bank === "part-2-question-bank") path = "";
      else if (bank === "duke-elder-question-bank")
        path = "/new_modules/duke-elder-question-bank/";

      // Fetch both in parallel
      const [uqRes, countsRes] = await Promise.all([
        GET_USER_QUESTIONS_WITH_META(withPerfTrace({ collectionPath: path })),
        GET_CATEGORY_YIELD_COUNTS(withPerfTrace({ collectionPath: path })),
      ]);

      // Cache counts for possibleQuestions computation
      categoryYieldCounts.value = countsRes?.data || {};

      // Return user questions with meta (as before)
      return uqRes.data;
    } catch (error) {
      console.log(error.message);
      console.log(error.code);
      alertStore.showAlert(
        "An error occurred. Please check your internet connection and try again.",
        "error",
      );
    }
  };

  const endSession = async (data) => (await END_SESSION(data)).data;


  return {
    userData,
    userStats,
    metaStats,
    userQuestions,
    flaggedQuestionIds,
    // NEW export
    categoryYieldCounts,
    getUserQuestionsWithMeta,
    endSession,
  };
});
